import { useCallback } from "react";
import { createUseStyles } from "react-jss";
import { useAsyncData } from "../../hooks/useAsyncData";
import { navigate } from "../../hooks/useRoute";
import { routeToHash } from "../../routes";
import { trpc } from "../../trpc";
import { Badge } from "../shared/Badge";
import { BoatCard, useBoatGridStyles } from "../shared/BoatCard";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { useLayoutStyles } from "../shared/styles";
import { BookingPolicyCard } from "./BookingPolicyCard";

const useStyles = createUseStyles({
  links: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    fontWeight: 600,
    fontSize: 14,
  },
});

/** Admin list of every boat, plus site-wide booking rules. */
export const AdminBoatsPage = () => {
  const classes = useStyles();
  const layout = useLayoutStyles();
  const grid = useBoatGridStyles();
  const load = useCallback(
    (signal: AbortSignal) => trpc.boat.list.query(undefined, { signal }),
    []
  );
  const boats = useAsyncData(load);

  return (
    <div className={layout.sections}>
      <Card
        title="Boats"
        actions={
          <Button
            onClick={() => navigate({ page: "adminBoat", boatId: "new" })}
          >
            Add boat
          </Button>
        }
      >
        {boats.error && (
          <LoadError message={boats.error} onRetry={boats.reload} />
        )}
        {boats.loading && !boats.data && <Loading />}
        {boats.data?.length === 0 && (
          <EmptyState title="No boats yet">
            Add a boat, then open dates for it on the Availability page.
          </EmptyState>
        )}
        {boats.data && boats.data.length > 0 && (
          <div className={grid.grid}>
            {boats.data.map((boat) => (
              <BoatCard
                key={boat.id}
                boat={boat}
                href={routeToHash({ page: "adminBoat", boatId: boat.id })}
                dimmed={!boat.isActive}
                badge={
                  boat.isActive ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="neutral">Inactive</Badge>
                  )
                }
              >
                <div className={classes.links}>
                  <a href={routeToHash({ page: "adminBoat", boatId: boat.id })}>
                    Edit
                  </a>
                  <a
                    href={routeToHash({
                      page: "availability",
                      boatId: boat.id,
                    })}
                  >
                    Availability
                  </a>
                  <a
                    href={routeToHash({
                      page: "boat",
                      boatId: boat.id,
                      trip: {},
                    })}
                  >
                    Calendar
                  </a>
                </div>
              </BoatCard>
            ))}
          </div>
        )}
      </Card>
      <BookingPolicyCard />
    </div>
  );
};
